import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { downloadFeeReceipt } from "@/lib/feeExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, CheckCircle2, FileText } from "lucide-react";
import { FREQ_LABEL } from "@/components/FeePlanDialog";
import { InvoiceDialog } from "@/components/InvoiceDialog";

export const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
export const fdate = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const INST_COLOR = { paid: "bg-emerald-100 text-emerald-800", overdue: "bg-rose-100 text-rose-800", partial: "bg-amber-100 text-amber-800", pending: "bg-slate-100 text-slate-700" };
export const REQ_LABEL = { submitted: "Submitted", under_review: "Under Review", approved: "Approved", rejected: "Rejected", payment_pending: "Payment Pending", payment_verified: "Payment Verified", verifying: "Verifying" };
export const REQ_COLOR = { submitted: "bg-blue-100 text-blue-800", under_review: "bg-amber-100 text-amber-800", approved: "bg-violet-100 text-violet-800", rejected: "bg-rose-100 text-rose-800", payment_pending: "bg-orange-100 text-orange-800", payment_verified: "bg-emerald-100 text-emerald-800", verifying: "bg-slate-100 text-slate-700" };

function PayRow({ plan, inst, onPaid }) {
  const [amount, setAmount] = useState(inst.balance);
  const [mode, setMode] = useState("cash");
  const [txn, setTxn] = useState("");
  const [late, setLate] = useState(inst.late_fee > 0);
  async function pay() {
    try {
      await api.post(`/fee-mgmt/plans/${plan.id}/installments/${inst.no}/pay`, { amount: Number(amount) + (late ? inst.late_fee : 0), mode, transaction_id: txn, include_late_fee: late });
      toast.success("Payment recorded"); onPaid();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 bg-slate-50 rounded-lg p-2">
      <Input data-testid={`ledger-pay-amount-${inst.no}`} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-28 h-8 bg-white" />
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger className="w-28 h-8 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>{["cash", "upi", "card", "bank", "cheque"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
      </Select>
      <Input placeholder="Txn / cheque no." value={txn} onChange={(e) => setTxn(e.target.value)} className="w-36 h-8 bg-white" />
      {inst.late_fee > 0 && <label className="text-xs flex items-center gap-1"><Checkbox checked={late} onCheckedChange={setLate} /> + late fee {inr(inst.late_fee)}</label>}
      <Button data-testid={`ledger-mark-paid-${inst.no}`} size="sm" onClick={pay} className="h-8 bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={14} className="mr-1" /> Mark paid</Button>
    </div>
  );
}

export function FeeLedgerDialog({ planId, onClose, canManage, onChanged }) {
  const [d, setD] = useState(null);
  const [payingNo, setPayingNo] = useState(null);
  const [inv, setInv] = useState(null);
  const load = () => api.get(`/fee-mgmt/plans/${planId}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (planId) { setD(null); setPayingNo(null); load(); } }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = d?.plan;

  return (
    <Dialog open={!!planId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">Fee ledger {p ? `• ${p.student_name}` : ""}</DialogTitle></DialogHeader>
        {!p ? <div className="py-10 text-center text-slate-400">Loading…</div> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="text-xs text-slate-500">{p.admission_number} • {p.class_name} {p.section} • {p.academic_year} • {FREQ_LABEL[p.frequency]}{p.kit_option ? ` • ${{ with_kit: "With Kit", without_kit: "Without Kit", dress_only: "Dress Only" }[p.kit_option]}` : ""}{p.customized === false ? " • Class default" : ""}</div>
              <Button size="sm" variant="outline" data-testid="ledger-invoice-btn" onClick={() => setInv(p.id)}><FileText size={14} className="mr-1" />Invoice</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[["Total fee", p.net_total, "text-slate-900"], ["Paid", p.paid, "text-emerald-700"], ["Pending", p.pending, "text-rose-700"], ["Late fee", p.late_fee_due, "text-orange-700"], ["Next due", null, "text-violet-800"]].map(([l, v, c]) => (
                <div key={l} data-testid={`ledger-${l.toLowerCase().replace(" ", "-")}`} className="rounded-xl bg-violet-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div>
                  <div className={`font-display font-bold mt-0.5 ${c}`}>{v === null ? fdate(p.next_due_date) : inr(v)}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500">Gross {inr(p.gross_total)}{p.discount_pct > 0 ? ` − discount ${p.discount_pct}% (${inr(p.gross_total * p.discount_pct / 100)})` : ""} − flat discount {inr(p.discount)} − scholarship {inr(p.scholarship)} − concession {inr(p.concession)}</div>
            <div>
              <div className="font-display font-bold text-slate-900 mb-2">Installments</div>
              <div className="space-y-2">
                {p.installments.map((i) => (
                  <div key={i.no} data-testid={`ledger-inst-${i.no}`} className="rounded-xl border border-slate-100 p-3">
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div><span className="font-semibold text-slate-900">{i.label}</span><span className="text-xs text-slate-500"> • due {fdate(i.due_date)}</span></div>
                      <div className="flex items-center gap-2 text-sm">
                        <span>{inr(i.paid)} / {inr(i.amount)}</span>
                        {i.late_fee > 0 && <span className="text-xs text-orange-700">+{inr(i.late_fee)} late</span>}
                        <Badge className={`${INST_COLOR[i.status]} capitalize`}>{i.status}</Badge>
                        {canManage && i.balance > 0 && <Button data-testid={`ledger-pay-${i.no}`} size="sm" variant="outline" onClick={() => setPayingNo(payingNo === i.no ? null : i.no)}>Record</Button>}
                      </div>
                    </div>
                    {payingNo === i.no && <PayRow plan={p} inst={i} onPaid={() => { setPayingNo(null); load(); onChanged?.(); }} />}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 mb-2">Payment history</div>
              {d.payments.length === 0 ? <p className="text-sm text-slate-500">No verified payments yet.</p> : (
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                  {d.payments.map((x) => (
                    <div key={x.id} data-testid={`ledger-payment-${x.id}`} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                      <span className="font-mono text-xs">{x.receipt_no}</span>
                      <span className="text-slate-500">{fdate(x.received_at)} • Inst {x.installment_no} • {x.mode.toUpperCase()}{x.transaction_id ? ` • ${x.transaction_id}` : ""}</span>
                      <span className="ml-auto font-semibold">{inr(x.amount)}</span>
                      <Button data-testid={`ledger-receipt-${x.id}`} size="sm" variant="outline" onClick={() => downloadFeeReceipt(x.id).catch((e) => toast.error(formatApiError(e)))}><FileDown size={14} /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {d.requests.length > 0 && (
              <div>
                <div className="font-display font-bold text-slate-900 mb-2">Parent payment requests</div>
                <div className="space-y-1">
                  {d.requests.map((q) => (
                    <div key={q.id} className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge className={REQ_COLOR[q.status]}>{REQ_LABEL[q.status]}</Badge>
                      <span>Inst {q.installment_no} • {inr(q.amount)} • Txn {q.transaction_id} • {fdate(q.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <InvoiceDialog planId={inv} onClose={() => setInv(null)} />
    </Dialog>
  );
}
